import { useState, useContext, useRef, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { UserAvatar, FileAttachment, ImageAttachment, Popup } from '../components';
import * as linkify from 'linkifyjs';
import linkifyHtml from 'linkify-html';
import { ChatContext } from '../contexts/ChatContext';
import { commentActions } from '../actions';
import { useMinuteCheck } from '../utils';
import { MessageProgressIcon } from './MessageProgressIcon';
import { DismissablePopup } from './popup/DismissablePopup';

function MessageBubble(props) {
  const {
    own,
    extra_heading_content,
    user,
    message,
    date,
    event_type,
    selectCommentToReply,
    id,
    highlighted,
    attachments,
    workspace_id,
    organization_id,
    content_group_id,
    renderParentComment,
    data,
    readOnly = false
  } = props;

  // Variables
  const dispatch = useDispatch();
  const userData = useMemo(() => user, [user?.id]);
  const created_at = new Date(data?.created_at);
  const minutesPassed = useMinuteCheck(created_at);

  // States
  const [selected, setSelected] = useState(false);
  const [showPopupDelete, setShowPopupDelete] = useState(false);

  // Refs
  const messageRef = useRef();
  const messageTextRef = useRef();

  // Contexts
  const {
    can_access_comment,
    setCommentToEdit,
    commentToEdit,
    handleEditComment
  } = useContext(ChatContext);

  // Selectors
  const {
    draft_comment_loading, 
    update_comment_loading, 
    draft_attachment_loading, 
    comment_tab_loading, 
    draft_render_loading
  } = useSelector(state => state.comment);
  const loading = draft_comment_loading || 
                  update_comment_loading || 
                  draft_attachment_loading || 
                  comment_tab_loading || 
                  draft_render_loading;
  const disabled = loading || 
                   commentToEdit?.id;

  // Functions
  const handleCloseDeletePopup = () => {
    setShowPopupDelete(false);
  }

  const handleCommentDelete = () => {
    const succesCallback = (response) => {
      handleCloseDeletePopup()
    }

    dispatch(commentActions.deleteComment(workspace_id, content_group_id, id, succesCallback))
  }

  const arrayChunk = (arr, n) => {
    const array = arr.slice();
    const chunks = [];
    while (array.length) chunks.push(array.splice(0, n));
    return chunks;
  };

  const full_message = () => {
    if(typeof message === 'undefined') return '';

    if ( data?.created_at === data?.updated_at ){
      return message
    } else if ( data?.deleted_at === null ) {
      return message + '<span class="message-edited"> (edited)</span>'
    } else {
      return message
    }
  }

  const tooltipHandler = (text) => {
    return {
      'data-toggle': "tooltip",
      title: text
    }
  }

  return (
    <div
      className={
        `message ${ own ? " message--own" : "message--other"}
        ${selected ? 'hovered' : null}
        ${highlighted ? 'highlighted' : null }`
      }
      ref={messageRef}
    >
      <div className="d-flex">
        <div className="message-user">
          {
            userData && 
            <UserAvatar
              icon={ userData?.profile}
              name={ userData?.full_name }
              name_initials={ userData?.name_initials }
              size="40"
              user_id={userData?.id}
              openUserSummary={true}
              workspace_id={workspace_id}
              organization_id={organization_id}
            />
          }

        </div>
        <div className="px-2" style={{width: "calc(100% - 40px)"}}>
          <div className="">
            <div className="description-container d-flex align-items-center">
              { extra_heading_content }
              { userData?.id && userData?.full_name &&
                <div 
                  className="description__user mb-1 cursor-pointer" 
                  tag-id={userData?.id}
                >
                  {userData?.full_name}
                </div>
              }

              <span className='ms-1 mb-1'><MessageProgressIcon type={event_type} /></span>
            </div>
          </div>

          <div className="description-container">
            {
              data?.parent &&
              <div className="mb-2">
                { renderParentComment(data) }
              </div>
            }

            {
              (message || event_type) &&

              <div className={ "description" + ( event_type ? " description--with-action" : "" ) }>
                <span 
                  ref={messageTextRef}
                  dangerouslySetInnerHTML={{ __html:
                    linkifyHtml((full_message() || ''), { target: '_blank' })
                  }} 
                />
              </div>
            }

            {
              attachments && attachments.length > 0 &&
              <div className="attachments-container">
                <div>
                  {
                    attachments.filter((attach) => attach.file_thumbnail).length > 0 &&
                      <div
                        className={`pr-0 mb-2`}
                      >

                        {
                          arrayChunk(attachments.filter((attach) => attach.file_thumbnail), 3).map((row, i) => (
                            <div key={i} className="mb-1 d-flex">
                              {row.map((attachment, i) => (
                                <ImageAttachment
                                  filename={ attachment.filename }
                                  thumbnail={ attachment.file_thumbnail }
                                  image_url={ attachment.image_url }
                                  attachment_id={ attachment.id }
                                  workspace_id={ workspace_id }
                                  content_group_id={ content_group_id }
                                  comment_id={ id }
                                  setSelected={ setSelected }
                                  key={ attachment.id }
                                  own={ own }
                                  viewable={ true }
                                  dynamic_icon={ true }
                                  confirm_deletion={true}
                                  className={attachments.filter((attach) => attach.file_thumbnail).length > 2 ? null : 'by_2'}
                                />
                              ))}
                            </div>
                          ))
                        }
                      </div>
                  }

                  {
                    attachments.filter((attach) => attach.file_thumbnail == null || attach.file_thumbnail == false).map((attachment, index) =>(
                      <div
                        key={ attachment.id }
                        className="pr-0"
                      >
                        <FileAttachment
                          filename={ attachment.filename }
                          workspace_id={ workspace_id }
                          content_group_id={ content_group_id }
                          comment_id={ id }
                          attachment_id={ attachment.id }
                          setSelected={ setSelected }
                          own={ own }
                          downloadable={ true }
                          confirm_deletion={true}
                          url={attachment.image_url}
                        />
                      </div>
                    ))
                  }

                </div>
              </div>
            }

            <div className="message-date">
              {
                can_access_comment && data?.deleted_at === null && readOnly === false &&
                <>
                  <div 
                    className={`message-reply-button mr-2 ${disabled ? 'disabled' : undefined}`} 
                    onClick={() => disabled ? undefined : selectCommentToReply(id, messageRef)}
                  >
                    Reply
                  </div>

                  { (own && !minutesPassed) &&
                    <>
                      <div 
                        className={`message-reply-button mr-2 ${disabled ? 'disabled' : undefined}`} 
                        onClick={() => disabled ? undefined : handleEditComment(data)}
                        {...tooltipHandler('Available within 5 minutes')}
                      >
                        Edit
                      </div>

                      <div 
                        className={`message-reply-button mr-2 ${disabled ? 'disabled' : undefined}`} 
                        onClick={() => disabled ? undefined : setShowPopupDelete(true)}
                        {...tooltipHandler('Available within 5 minutes')}
                      >
                        Delete
                      </div>
                    </>
                  }
                </>
              }



              { date }
            </div>
          </div>
        </div>
      </div>

      <DismissablePopup
        show={showPopupDelete}
        onClose={handleCloseDeletePopup}
        onConfirm={handleCommentDelete}
        title={'Delete Comment'}
        message={'You cannot undo this action. Are you sure you want to delete?'}
        confirmText='Delete'
        dismissable={false}
      />
    </div>
  );
}

export { MessageBubble };
